import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { apiService } from '@/api';

/**
 * Recovery / exit offer state — server-driven.
 *
 * The 10-minute deadline is owned by the backend (Users.exit_offer_expires_at,
 * set by the v2-exit-offer lambda) and returned as an absolute `expiresAt`
 * (unix ms). This store only mirrors it; create-checkout-session independently
 * re-validates the deadline, so the timer the user sees is REAL — it cannot be
 * extended by editing local state, and the discount dies with the server clock.
 *
 * We persist only the absolute `expiresAt` + `showBanner` to sessionStorage so a
 * page reload restores the banner instantly (no flash, no extra request); the
 * absolute deadline keeps the countdown accurate across reloads.
 */

interface ExitOfferState {
  /** Full-screen overlay — shown once, at the moment the user leaves the paywall. */
  showOverlay: boolean;
  /** Slim top banner — persists across pages after the overlay is dismissed. */
  showBanner: boolean;
  /** Absolute server deadline in unix ms (0 = no live offer). */
  expiresAt: number;
  /** Guards against duplicate start calls while one is in flight. */
  requesting: boolean;

  /** Ask the server to start the offer (leaving the paywall without a checkout). */
  requestExitOffer: (userId: string) => Promise<void>;
  /** Dismiss the overlay but keep counting in the banner. */
  dismissOverlay: () => void;
  /** End the offer for good (timer hit 0, expired, or purchased). */
  endOffer: () => void;
}

export const useExitOfferStore = create<ExitOfferState>()(
  persist(
    (set, get) => ({
      showOverlay: false,
      showBanner: false,
      expiresAt: 0,
      requesting: false,

      requestExitOffer: async (userId: string) => {
        const s = get();
        // Already live (overlay/banner up or a running deadline) → don't re-ask.
        if (s.requesting || s.showOverlay || s.showBanner) return;
        if (s.expiresAt && Date.now() < s.expiresAt) return;
        if (!userId) return;
        set({ requesting: true });
        try {
          const { eligible, expiresAt } = await apiService.exitOffer(userId, 'start');
          const deadlineMs = expiresAt * 1000;
          if (eligible && deadlineMs > Date.now()) {
            set({ showOverlay: true, showBanner: false, expiresAt: deadlineMs });
          }
        } catch {
          /* best-effort: a failed start just means no offer this time */
        } finally {
          set({ requesting: false });
        }
      },

      dismissOverlay: () => set({ showOverlay: false, showBanner: true }),

      endOffer: () => set({ showOverlay: false, showBanner: false, expiresAt: 0 }),
    }),
    {
      name: 'vivideo-exit-offer',
      // sessionStorage: the offer is a single-session experience (matches the
      // previous store). An absolute expiry means the restored timer stays exact.
      storage: {
        getItem: (name) => {
          const str = sessionStorage.getItem(name);
          if (!str) return null;
          const parsed = JSON.parse(str);
          const expiresAt = parsed?.state?.expiresAt || 0;
          if (!expiresAt || Date.now() >= expiresAt) {
            sessionStorage.removeItem(name);
            return null;
          }
          return parsed;
        },
        setItem: (name, value) => sessionStorage.setItem(name, JSON.stringify(value)),
        removeItem: (name) => sessionStorage.removeItem(name),
      },
      // Never restore the full-screen overlay on reload — only the slim banner.
      partialize: (state) => ({
        showBanner: state.showBanner || state.showOverlay,
        expiresAt: state.expiresAt,
      } as unknown as ExitOfferState),
    }
  )
);
